import { supabase, handleSupabaseError } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { OrderItem } from '@/types/models/Order';

// Types for customer orders
export interface CustomerOrder {
  id: string;
  store_id: string;
  customer_phone: string;
  customer_name: string | null;
  customer_name_locale: string | null;
  status: string;
  order_number: string | null;
  invoice_id: string | null;
  converted_at: string | null;
  order_instructions: string | null;
  delivery_address: string | null;
  delivery_address_locale: string | null;
  created_at: string;
  updated_at: string;
  expires_at: string | null;
}

export interface CustomerOrderItem {
  id: string;
  order_id: string;
  item_id: string | null;
  quantity: number;
  created_at: string;
}

export class CustomerOrderService {
  /**
   * Place an order from customer's client-side cart
   * Creates order in database with status 'Placed'
   */
  static async placeOrder(
    storeId: string,
    customerPhone: string,
    items: OrderItem[],
    options?: {
      customerName?: string;
      customerNameLocale?: string;
      orderInstructions?: string | null;
      deliveryAddress?: string | null;
      deliveryAddressLocale?: string | null;
    }
  ): Promise<CustomerOrder> {
    try {
      logger.info('Customer placing order', { storeId, customerPhone, itemCount: items.length, hasInstructions: !!options?.orderInstructions });

      // Validate inputs - allow order with either items OR instructions
      if (items.length === 0 && !options?.orderInstructions?.trim()) {
        throw new Error('Order must contain at least one item or order instructions');
      }

      if (!storeId || typeof storeId !== 'string') {
        throw new Error('Invalid store ID');
      }

      if (!customerPhone || typeof customerPhone !== 'string') {
        throw new Error('Invalid customer phone number');
      }

      // Generate unique order number
      const orderNumber = `ORD-${storeId.slice(0, 8)}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

      // Create order in database
      const { data: order, error: orderError } = await supabase
        .from('customer_orders')
        .insert([{
          store_id: storeId,
          customer_phone: customerPhone,
          customer_name: options?.customerName || null,
          customer_name_locale: options?.customerNameLocale || null,
          status: 'Placed',
          order_number: orderNumber,
          order_instructions: options?.orderInstructions || null,
          delivery_address: options?.deliveryAddress || null,
          delivery_address_locale: options?.deliveryAddressLocale || null,
        }])
        .select()
        .single();

      if (orderError) {
        logger.error('Failed to create order', { error: orderError });
        throw orderError;
      }

      // Create order items
      const orderItemsData = items.map(item => ({
        order_id: order.id,
        item_id: item.itemId,
        quantity: item.quantity,
      }));

      const { error: itemsInsertError } = await supabase
        .from('customer_order_items')
        .insert(orderItemsData);

      if (itemsInsertError) {
        logger.error('Failed to create order items', { error: itemsInsertError, orderId: order.id });
        // Clean up the order if items insertion fails
        const { error: deleteError } = await supabase
          .from('customer_orders')
          .delete()
          .eq('id', order.id);
        
        if (deleteError) {
          logger.error('Failed to cleanup order after items insertion failure', { error: deleteError, orderId: order.id });
        }
        
        throw itemsInsertError;
      }

      logger.info('Customer order placed successfully', { orderId: order.id, orderNumber });

      return order;
    } catch (error) {
      logger.error('Error in CustomerOrderService.placeOrder', { error, storeId, customerPhone });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get customer's order by ID
   * Only returns order if it belongs to the customer (via customer_phone)
   */
  static async getCustomerOrderById(
    orderId: string,
    customerPhone: string
  ): Promise<CustomerOrder & { items: CustomerOrderItem[] }> {
    try {
      // Validate inputs
      if (!orderId || typeof orderId !== 'string') {
        throw new Error('Invalid order ID');
      }

      if (!customerPhone || typeof customerPhone !== 'string') {
        throw new Error('Invalid customer phone number');
      }

      logger.info('Customer fetching order by ID', { orderId, customerPhone });

      const { data: order, error: orderError } = await supabase
        .from('customer_orders')
        .select(`
          *,
          items:customer_order_items(*)
        `)
        .eq('id', orderId)
        .eq('customer_phone', customerPhone)
        .single();

      if (orderError) {
        logger.error('Failed to fetch customer order', { error: orderError, orderId, customerPhone });
        throw orderError;
      }

      if (!order) {
        throw new Error('Order not found or does not belong to you');
      }

      return { ...order, items: (order.items as CustomerOrderItem[]) || [] };
    } catch (error) {
      logger.error('Error in CustomerOrderService.getCustomerOrderById', { error, orderId, customerPhone });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get all orders for a customer
   * @param customerPhone - Customer's phone number
   * @param options - Optional filters (status, limit)
   * @returns Array of orders with their items
   */
  static async getCustomerOrders(
    customerPhone: string,
    options?: {
      status?: string;
      limit?: number;
    }
  ): Promise<(CustomerOrder & { items: CustomerOrderItem[] })[]> {
    try {
      // Validate inputs
      if (!customerPhone || typeof customerPhone !== 'string') {
        throw new Error('Invalid customer phone number');
      }

      if (options?.limit && (options.limit < 1 || !Number.isInteger(options.limit))) {
        throw new Error('Limit must be a positive integer');
      }

      logger.info('Customer fetching orders', { customerPhone, options });

      let query = supabase
        .from('customer_orders')
        .select(`
          *,
          items:customer_order_items(*)
        `)
        .eq('customer_phone', customerPhone)
        .order('created_at', { ascending: false });

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data: orders, error: ordersError } = await query;

      if (ordersError) {
        logger.error('Failed to fetch customer orders', { error: ordersError, customerPhone });
        throw ordersError;
      }

      if (!orders || orders.length === 0) {
        return [];
      }

      // Map orders to ensure items array is present and properly typed
      return orders.map(order => ({
        ...order,
        items: (order.items as CustomerOrderItem[]) || []
      }));
    } catch (error) {
      logger.error('Error in CustomerOrderService.getCustomerOrders', { error, customerPhone });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get active (non-terminal) orders for a customer at a specific store
   * Non-terminal statuses: 'Placed', 'Confirmed', 'Processing', 'Ready'
   * @param customerPhone - Customer's phone number
   * @param storeId - Store ID
   * @returns Array of active orders with their items
   */
  static async getActiveOrdersForStore(
    customerPhone: string,
    storeId: string
  ): Promise<(CustomerOrder & { items: CustomerOrderItem[] })[]> {
    try {
      // Validate inputs
      if (!customerPhone || typeof customerPhone !== 'string') {
        throw new Error('Invalid customer phone number');
      }

      if (!storeId || typeof storeId !== 'string') {
        throw new Error('Invalid store ID');
      }

      logger.info('Customer fetching active orders for store', { customerPhone, storeId });

      // Non-terminal statuses
      const activeStatuses = ['Placed', 'Confirmed', 'Processing', 'Ready'];

      const { data: orders, error: ordersError } = await supabase
        .from('customer_orders')
        .select(`
          *,
          items:customer_order_items(*)
        `)
        .eq('customer_phone', customerPhone)
        .eq('store_id', storeId)
        .in('status', activeStatuses)
        .order('created_at', { ascending: false });

      if (ordersError) {
        logger.error('Failed to fetch active orders', { error: ordersError, customerPhone, storeId });
        throw ordersError;
      }

      if (!orders || orders.length === 0) {
        return [];
      }

      // Map orders to ensure items array is present and properly typed
      return orders.map(order => ({
        ...order,
        items: (order.items as CustomerOrderItem[]) || []
      }));
    } catch (error) {
      logger.error('Error in CustomerOrderService.getActiveOrdersForStore', { error, customerPhone, storeId });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get all orders for a store (seller view)
   * @param storeId - Store ID
   * @param options - Optional filters (status, limit)
   * @returns Array of orders with their items
   */
  static async getOrdersByStore(
    storeId: string,
    options?: {
      status?: string;
      limit?: number;
    }
  ): Promise<(CustomerOrder & { items: CustomerOrderItem[] })[]> {
    try {
      // Validate inputs
      if (!storeId || typeof storeId !== 'string') {
        throw new Error('Invalid store ID');
      }

      if (options?.limit && (options.limit < 1 || !Number.isInteger(options.limit))) {
        throw new Error('Limit must be a positive integer');
      }

      logger.info('Seller fetching orders for store', { storeId, options });

      let query = supabase
        .from('customer_orders')
        .select(`
          *,
          items:customer_order_items(*)
        `)
        .eq('store_id', storeId)
        .order('created_at', { ascending: false });

      if (options?.status) {
        query = query.eq('status', options.status);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data: orders, error: ordersError } = await query;

      if (ordersError) {
        logger.error('Failed to fetch store orders', { error: ordersError, storeId });
        throw ordersError;
      }

      if (!orders || orders.length === 0) {
        return [];
      }

      // Map orders to ensure items array is present and properly typed
      return orders.map(order => ({
        ...order,
        items: (order.items as CustomerOrderItem[]) || []
      }));
    } catch (error) {
      logger.error('Error in CustomerOrderService.getOrdersByStore', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Get count of placed and processing orders from the last 7 days
   * Optimized query that directly counts without fetching all order data
   * @param storeId - Store ID
   * @returns Count of placed and processing orders from last 7 days
   */
  static async getRecentPlacedAndProcessingCount(storeId: string): Promise<number> {
    try {
      if (!storeId || typeof storeId !== 'string') {
        throw new Error('Invalid store ID');
      }

      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      const sevenDaysAgoISO = sevenDaysAgo.toISOString();

      const { count, error } = await supabase
        .from('customer_orders')
        .select('*', { count: 'exact', head: true })
        .eq('store_id', storeId)
        .in('status', ['Placed', 'Processing'])
        .gte('created_at', sevenDaysAgoISO);

      if (error) {
        logger.error('Failed to count recent orders', { error, storeId });
        throw error;
      }

      return count || 0;
    } catch (error) {
      logger.error('Error in CustomerOrderService.getRecentPlacedAndProcessingCount', { error, storeId });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Convert a customer order to a Draft invoice
   * Called when store accepts an order
   * Payment method is always 'Cash' (same as CreateInvoice default)
   * Payment tracking is handled on the invoice, not on the order
   * @param orderId - Order ID to convert
   * @returns Invoice ID of the created Draft invoice
   */
  static async convertToInvoice(
    orderId: string
  ): Promise<string> {
    try {
      if (!orderId || typeof orderId !== 'string') {
        throw new Error('Invalid order ID');
      }

      logger.info('Converting order to invoice', { orderId });

      // Payment method is set to 'Cash' in SQL function (same as CreateInvoice default)
      // Payment tracking is handled on invoice when it's processed (track_payment defaults to false)
      const { data: invoiceId, error: convertError } = await supabase.rpc(
        'convert_customer_order_to_invoice',
        {
          p_order_id: orderId,
        }
      );

      if (convertError) {
        logger.error('Failed to convert order to invoice', { error: convertError, orderId });
        throw convertError;
      }

      if (!invoiceId) {
        throw new Error('Invoice ID not returned from conversion function');
      }

      logger.info('Order converted to invoice successfully', { orderId, invoiceId });

      return invoiceId as string;
    } catch (error) {
      logger.error('Error in CustomerOrderService.convertToInvoice', { error, orderId });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Reject an order (only for 'Placed' status orders)
   * Updates order status to 'Cancelled'
   * @param orderId - Order ID to reject
   */
  static async rejectOrder(orderId: string): Promise<void> {
    try {
      if (!orderId || typeof orderId !== 'string') {
        throw new Error('Invalid order ID');
      }

      logger.info('Rejecting order', { orderId });

      const { error } = await supabase
        .from('customer_orders')
        .update({ status: 'Cancelled' })
        .eq('id', orderId)
        .eq('status', 'Placed'); // Only allow rejecting Placed orders

      if (error) {
        logger.error('Failed to reject order', { error, orderId });
        throw error;
      }

      logger.info('Order rejected successfully', { orderId });
    } catch (error) {
      logger.error('Error in CustomerOrderService.rejectOrder', { error, orderId });
      throw handleSupabaseError(error);
    }
  }

  /**
   * Abandon an order and its associated invoice
   * Updates order status to 'Cancelled' and invoice status to 'Abandoned'
   * @param orderId - Order ID to abandon
   */
  static async abandonOrder(orderId: string): Promise<void> {
    try {
      if (!orderId || typeof orderId !== 'string') {
        throw new Error('Invalid order ID');
      }

      logger.info('Abandoning order', { orderId });

      // First, get the order to check if it has an invoice
      const { data: order, error: fetchError } = await supabase
        .from('customer_orders')
        .select('invoice_id')
        .eq('id', orderId)
        .single();

      if (fetchError) {
        logger.error('Failed to fetch order for abandonment', { error: fetchError, orderId });
        throw fetchError;
      }

      if (!order) {
        throw new Error('Order not found');
      }

      // Update order status to Cancelled
      const { error: orderError } = await supabase
        .from('customer_orders')
        .update({ status: 'Cancelled' })
        .eq('id', orderId);

      if (orderError) {
        logger.error('Failed to update order status', { error: orderError, orderId });
        throw orderError;
      }

      // If order has an invoice, abandon it too
      if (order.invoice_id) {
        const { error: invoiceError } = await supabase
          .from('invoices')
          .update({ status: 'Abandoned' })
          .eq('id', order.invoice_id);

        if (invoiceError) {
          logger.error('Failed to abandon invoice', { error: invoiceError, invoiceId: order.invoice_id });
          throw invoiceError;
        }

        logger.info('Order and invoice abandoned successfully', { orderId, invoiceId: order.invoice_id });
      } else {
        logger.info('Order abandoned successfully', { orderId });
      }
    } catch (error) {
      logger.error('Error in CustomerOrderService.abandonOrder', { error, orderId });
      throw handleSupabaseError(error);
    }
  }
}

